<template>
  <modal
    name="desconto-form"
    height="auto"
    width="915"
    :scrollable="true"
    :clickToClose="false"
  >
    <CloseModal :close="close" />
    <div class="modalbox">
      <div class="modalbox-content">
        <h5 class="form-title">{{ isEdit ? 'Editar desconto' : 'Novo desconto' }}</h5>

        <CAlert v-if="isRegraDoErp" show color="warning" class="mb-2">
          <strong>Regra importada do ERP.</strong>
          Ao salvar aqui ela passa a ser mantida no VersoADM, e a carga do ERP
          deixa de atualizá-la.
        </CAlert>

        <CTabs variant="tabs" class="nav-tabs-boxed">
          <CTab title="Informações">
            <div class="row">
              <div class="col-6 col-md-3">
                <label class="form-label">Cód <span class="req">*</span></label>
                <input
                  type="text"
                  class="form-control"
                  v-model="form.id"
                  :disabled="isEdit"
                  placeholder="Código no ERP"
                />
              </div>
              <div class="col-6 col-md-3">
                <label class="form-label">Ativo</label>
                <select class="form-control" v-model.number="form.ativo">
                  <option :value="1">Sim</option>
                  <option :value="0">Não</option>
                </select>
              </div>
              <div class="col-6 col-md-3">
                <label class="form-label">Dt. início <span class="req">*</span></label>
                <InputDatePicker v-model="form.dtinicio" />
              </div>
              <div class="col-6 col-md-3">
                <label class="form-label">Dt. fim <span class="req">*</span></label>
                <InputDatePicker v-model="form.dtfim" />
              </div>
              <div class="col-12">
                <label class="form-label">Descrição</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="form.descricao"
                  placeholder="Como este desconto aparece para a operação"
                />
              </div>
            </div>

            <h6 class="section-label">Aplicar nos sistemas</h6>
            <div class="cards-grid">
              <SelectableCard
                v-for="canal in canais"
                :key="canal.key"
                :value="canal.key"
                :label="canal.label"
                :icon="canal.icon"
                :help="hints[canal.key]"
                :selected="!!form[canal.key]"
                @select="alternarCanal(canal.key)"
              />
            </div>

          </CTab>

          <CTab title="Regras">
            <h6 class="section-label">Tipo de regra</h6>
            <div class="cards-grid">
              <SelectableCard
                v-for="opcao in opcoesTipoRegra"
                :key="opcao.value"
                :value="opcao.value"
                :label="opcao.label"
                :description="opcao.description"
                :icon="opcao.icon"
                :selected="tipoRegraSelecionado === opcao.value"
                @select="selecionarTipoRegra"
              />
            </div>

            <h6 class="section-label">Configurações</h6>
            <div class="toggles-box">
              <BooleanToggle
                v-if="form.tipo === 'SEGMENTACAO'"
                label="Usar validação por regra?"
                help="Ligado, cada condição tem a sua própria faixa em R$. Desligado, vale a faixa geral da regra."
                :value="!!form.validar_por_regra"
                @input="definirBooleano('validar_por_regra', $event)"
              />
              <BooleanToggle
                v-if="isRegraPorValor"
                :label="form.tipo === 'MIX'
                  ? 'Pedido precisa ter todos os produtos?'
                  : 'Todas as condições precisam ser válidas?'"
                :help="form.tipo === 'MIX'
                  ? 'Ligado, o pedido precisa conter todos os produtos do mix para a regra valer.'
                  : 'Ligado, o pedido precisa atender a todas as condições da regra. Desligado, basta uma delas.'"
                :value="!!form.validar_todas_regras"
                @input="definirBooleano('validar_todas_regras', $event)"
              />
              <BooleanToggle
                label="Aplicar o desconto automaticamente?"
                :help="hints.aplicaautomatico"
                :value="!!form.aplicaautomatico"
                @input="definirBooleano('aplicaautomatico', $event)"
              />
              <BooleanToggle
                label="Tem prioridade sobre os outros descontos?"
                :help="hints.prioritaria"
                :value="!!form.prioritaria"
                @input="definirBooleano('prioritaria', $event)"
              />
              <BooleanToggle
                label="Tem prioridade máxima (ignora os demais)?"
                :help="hints.prioritariageral"
                :value="!!form.prioritariageral"
                @input="definirBooleano('prioritariageral', $event)"
              />
              <BooleanToggle
                label="Mostrar o preço antigo riscado ao cliente?"
                :help="hints.visivel"
                :value="!!form.visivel"
                @input="definirBooleano('visivel', $event)"
              />
              <BooleanToggle
                label="Exibir a etiqueta de percentual no produto?"
                :help="hints.exibeflag"
                :value="!!form.exibeflag"
                @input="definirBooleano('exibeflag', $event)"
              />
              <BooleanToggle
                label="Aplicar sobre o preço de tabela (acumula)?"
                :help="hints.alteraprecotabela"
                :value="!!form.alteraprecotabela"
                @input="definirBooleano('alteraprecotabela', $event)"
              />
            </div>

            <h6 class="section-label">Parâmetros de aplicação</h6>
            <div class="row row--aplicacao">
              <!--
                Segmento e Mix validam por faixa de valor; só o desconto padrão
                valida por quantidade. Deixar os dois pares na tela ao mesmo tempo
                daria à operação duas faixas concorrentes para a mesma regra.
              -->
              <div class="col-4 col-md-2 campo-qtd" v-if="!isRegraPorValor">
                <label class="form-label">Qtd. Mín <span class="req">*</span></label>
                <input
                  type="number"
                  class="form-control"
                  v-model.number="form.qtdmin"
                  placeholder="0"
                />
              </div>
              <div class="col-4 col-md-2 campo-qtd" v-if="!isRegraPorValor">
                <label class="form-label">Qtd. Máx</label>
                <input
                  type="number"
                  class="form-control"
                  v-model.number="form.qtdmax"
                  placeholder="Sem limite"
                />
              </div>
              <!--
                Percentual e teto vivem no cabeçalho só no desconto padrão. Em
                Segmento e Mix eles ficam em cada condição ou em cada produto do
                mix; mantê-los aqui daria ao vendedor dois tetos concorrentes para
                o mesmo item, sem nada no servidor decidindo entre eles.
              -->
              <div class="col-4 col-md-2" v-if="!isRegraPorValor">
                <label class="form-label">
                  % Desc. <span class="req">*</span>
                  <HintInfo :texto="hints.percdesconto" />
                </label>
                <input-percent class="form-control" v-model="form.percdesconto" />
              </div>
              <div class="col-4 col-md-2" v-if="!isRegraPorValor">
                <label class="form-label">
                  Perc. Máx Desc
                  <HintInfo :texto="hints.percmaximodesc" />
                </label>
                <input-percent class="form-control" v-model="form.percmaximodesc" />
              </div>
              <!-- Faixa da regra inteira. Com faixa por condição ela sai de cena:
                   quem guarda os valores passa a ser cada condição. -->
              <div class="col-6 col-md-3" v-if="isRegraPorValor && !form.validar_por_regra">
                <label class="form-label">Valor mínimo do pedido</label>
                <InputMoney v-model="form.valorminimo" />
              </div>
              <div class="col-6 col-md-3" v-if="isRegraPorValor && !form.validar_por_regra">
                <label class="form-label">Valor máximo do pedido</label>
                <InputMoney v-model="form.valormaximo" />
              </div>
              <!-- Só a regra por valor pode ser condicionada ao frete. -->
              <div class="col-6 col-md-3" v-if="isRegraPorValor">
                <label class="form-label" for="desconto-tipofrete">Tipo de frete</label>
                <select
                  id="desconto-tipofrete"
                  class="form-control"
                  v-model="form.tipofrete_id"
                >
                  <option :value="null">Todos</option>
                  <option v-for="frete in tiposFrete" :key="frete.id" :value="String(frete.id)">
                    {{ frete.descricao }}
                  </option>
                </select>
              </div>
              <div class="col-4 col-md-3">
                <label class="form-label">
                  Cód. Política
                  <HintInfo :texto="hints.politica_id" />
                </label>
                <input
                  type="text"
                  class="form-control"
                  v-model="form.politica_id"
                  placeholder="Código no ERP"
                />
              </div>
            </div>

            <template v-if="isRegraPorValor">

              <template v-if="form.tipo === 'SEGMENTACAO'">
                <h6 class="section-label">Condições</h6>
                <DescontoCondicoes
                  :condicoes="form.dimensoes"
                  :dimensoes="dimensoesVisiveis"
                  :validar-por-regra="!!form.validar_por_regra"
                  :validar-todas-regras="!!form.validar_todas_regras"
                />
              </template>

              <template v-if="form.tipo === 'MIX'">
                <h6 class="section-label">Produtos do mix</h6>
                <ListaProdutos
                  :campanha="{}"
                  :itemsSelected="form.mixProdutos"
                  permite-negativo
                  :select-callback="adicionarProdutoMix"
                  :deselect-callback="removerProdutoMix"
                  :updatePercentageCallback="atualizarPercentualMix"
                  permite-teto
                  :updateTetoCallback="atualizarTetoMix"
                />
              </template>
            </template>

          </CTab>

          <CTab title="Conta corrente">
            <h6 class="section-label">Conta corrente do RCA</h6>
            <div class="toggles-box">
              <BooleanToggle
                label="Creditar ao RCA se não conceder o desconto total?"
                :help="hints.creditacontacorrente"
                :value="!!form.creditacontacorrente"
                @input="definirBooleano('creditacontacorrente', $event)"
              />
              <BooleanToggle
                label="Debitar do RCA se conceder o desconto?"
                :help="hints.politicacontacorrente"
                :value="!!form.politicacontacorrente"
                @input="definirBooleano('politicacontacorrente', $event)"
              />
            </div>
          </CTab>

          <CTab title="Restrições · Cliente">
            <p class="restricao-ajuda">
              Campo em branco vale para todos. Preencher restringe o desconto ao que for informado.
            </p>

            <h6 class="section-label">Cliente</h6>
            <div class="row">
              <div class="col-12 col-md-6">
                <label class="form-label" for="desconto-cliente">Cód. Cliente</label>
                <ComboCodigo
                  input-id="desconto-cliente"
                  v-model="form.cliente_id"
                  :opcoes="opcoesAsync('cliente_id')"
                  :carregando="busca.cliente_id.buscando"
                  busca-async
                  placeholder="Digite o código ou a razão social"
                  @search="onBuscaAsync('cliente_id', $event)"
                />
              </div>

            </div>

            <h6 class="section-label">Características</h6>
            <div class="row">
              <div
                class="col-12 col-md-3"
                v-for="campo in camposRestricaoCliente"
                :key="campo.campo"
              >
                <label class="form-label" :for="`desconto-${campo.campo}`">{{ campo.label }}</label>
                <ComboCodigo
                  :input-id="`desconto-${campo.campo}`"
                  v-model="form[campo.campo]"
                  :opcoes="opcoesRestricao(campo)"
                  :codigo-livre="!!campo.codigoLivre"
                  placeholder="—"
                />
              </div>
            </div>

            <h6 class="section-label">Uso da política</h6>
            <div class="toggles-box">
              <BooleanToggle
                label="Validar o código do RCA?"
                :help="hints.validausuario"
                :value="!!form.validausuario"
                @input="definirBooleano('validausuario', $event)"
              />
            </div>
          </CTab>

          <CTab title="Restrições · Produto">
            <p class="restricao-ajuda">
              Campo em branco vale para todos. Preencher restringe o desconto ao que for informado.
            </p>

            <h6 class="section-label">Produto</h6>
            <div class="row">
              <div class="col-12 col-md-4">
                <label class="form-label" for="desconto-produto">Cód. Produto</label>
                <ComboCodigo
                  input-id="desconto-produto"
                  v-model="form.produto_id"
                  :opcoes="opcoesAsync('produto_id')"
                  :carregando="busca.produto_id.buscando"
                  busca-async
                  placeholder="Digite o código ou a descrição"
                  @search="onBuscaAsync('produto_id', $event)"
                />
              </div>
              <div class="col-12 col-md-4">
                <label class="form-label" for="desconto-produto-principal">
                  Cód. Prod. Principal
                  <HintInfo :texto="hints.produtoprincipal_id" />
                </label>
                <ComboCodigo
                  input-id="desconto-produto-principal"
                  v-model="form.produtoprincipal_id"
                  :opcoes="opcoesAsync('produtoprincipal_id')"
                  :carregando="busca.produtoprincipal_id.buscando"
                  busca-async
                  placeholder="Digite o código ou a descrição"
                  @search="onBuscaAsync('produtoprincipal_id', $event)"
                />
              </div>
              <div class="col-12 col-md-4">
                <label class="form-label" for="desconto-fornecedor">Cód. Fornecedor</label>
                <ComboCodigo
                  input-id="desconto-fornecedor"
                  v-model="form.fornecedor_id"
                  :opcoes="opcoesRestricao(camposExtra.fornecedor)"
                  :codigo-livre="!!camposExtra.fornecedor.codigoLivre"
                  placeholder="—"
                />
              </div>
            </div>
            <h6 class="section-label">Dimensões</h6>
            <div class="row" v-if="dimensoesVisiveis.length">
              <div
                class="col-12 col-md-3"
                v-for="dimensao in dimensoesVisiveis"
                :key="`dim-${dimensao.coluna}`"
              >
                <label class="form-label" :for="`desconto-dimensao${dimensao.coluna}`">{{ dimensao.label }}</label>
                <ComboCodigo
                  :input-id="`desconto-dimensao${dimensao.coluna}`"
                  v-model="form[`dimensao${dimensao.coluna}`]"
                  :opcoes="opcoesDimensao(dimensao)"
                  placeholder="—"
                />
              </div>
            </div>
            <div class="row" v-else>
              <div class="col-12 col-md-3" v-for="n in totalDimensoes" :key="`dim-txt-${n}`">
                <label class="form-label" :for="`desconto-dim-txt-${n}`">Cód. Dim. {{ n }}</label>
                <input
                  :id="`desconto-dim-txt-${n}`"
                  type="text"
                  class="form-control"
                  v-model="form[`dimensao${n}`]"
                  placeholder="Todas"
                />
              </div>
            </div>
          </CTab>

          <CTab title="Restrições · Geral">
            <p class="restricao-ajuda">
              Campo em branco vale para todos. Preencher restringe o desconto ao que for informado.
            </p>

            <h6 class="section-label">Pagamento e preço</h6>
            <div class="row">
              <div class="col-12 col-md-3">
                <label class="form-label" for="desconto-plano">Cód. Plano</label>
                <ComboCodigo
                  input-id="desconto-plano"
                  v-model="form.planopagamento_id"
                  :opcoes="opcoesRestricao(camposExtra.plano)"
                  placeholder="—"
                />
              </div>
              <div class="col-12 col-md-3">
                <label class="form-label">Múltiplo</label>
                <input
                  type="number"
                  class="form-control"
                  v-model.number="form.multiplo"
                  placeholder="Sem múltiplo"
                />
              </div>
              <div class="col-12 col-md-3">
                <label class="form-label" for="desconto-tabelapreco">Tabela de Preço</label>
                <ComboCodigo
                  input-id="desconto-tabelapreco"
                  v-model="form.tabelapreco"
                  :opcoes="opcoesRestricao(camposExtra.tabelapreco)"
                  placeholder="—"
                />
              </div>
              <div class="col-12 col-md-3">
                <label class="form-label">Num. Col. preço</label>
                <select class="form-control" v-model="form.coluna_preco">
                  <option :value="null">Todas</option>
                  <option v-for="n in 7" :key="n" :value="String(n)">Preço {{ n }}</option>
                </select>
              </div>
            </div>

            <h6 class="section-label">Entrega</h6>
            <div class="toggles-box">
              <BooleanToggle
                label="Somente retira?"
                :value="!!form.somenteretira"
                @input="definirBooleano('somenteretira', $event)"
              />
            </div>
          </CTab>
        </CTabs>
      </div>
    </div>
    <div class="modal-footer-bar">
      <CButton variant="outline" color="secondary" @click="close">Cancelar</CButton>
      <CButton color="primary" @click="submit" :disabled="saving">
        {{ saving ? 'Salvando...' : (isEdit ? 'Salvar alterações' : 'Cadastrar') }}
      </CButton>
    </div>
  </modal>
</template>

<script>
import CloseModal from "@/components/CloseModal";
import InputDatePicker from "@/components/InputDatePicker";
import _debounce from "lodash/debounce";
import { get, post, put } from "@/helpers/apiRequest";
import ComboCodigo from "@/components/form/ComboCodigo";
import HintInfo from "@/components/form/HintInfo";
import DescontoCondicoes from "./DescontoCondicoes";
import ListaProdutos from "@/views/Campanhas/components/CampanhasComerciais/components/Regra/ListaProdutos";
import InputMoney from "@/components/InputMoney";
import SelectableCard from "@/views/Campanhas/components/CampanhasComerciais/shared/SelectableCard";
import BooleanToggle from "@/views/Campanhas/components/CampanhasComerciais/shared/BooleanToggle";
import { CANAIS } from "@/views/Campanhas/components/CampanhasComerciais/helpers/campanhaLabels";
import { HINTS_DESCONTO } from "./helpers/hintsDesconto";
import {
  CAMPOS_RESTRICAO_CLIENTE,
  CAMPOS_RESTRICAO_EXTRA,
  CAMPOS_RESTRICAO_TODOS,
  TOTAL_DIMENSOES,
  dimensoesComValoresDoForm,
  opcoesClientes,
  opcoesComValorAtual,
  opcoesDe,
  opcoesDimensaoComValorAtual,
  patchValoresDimensoes,
  patchValoresRestricoes,
} from "./helpers/restricoesDesconto";

/**
 * Campos cujo combo busca no servidor: cada um tem estado proprio (termo,
 * opcoes e flag de carregando), para a digitacao num campo nao trocar a lista
 * nem acender o "Buscando..." do outro.
 */
const CAMPOS_BUSCA_ASYNC = {
  cliente_id: {
    url: '/admin/descontos-clientes',
    // findClientesWithLimit responde paginado: as opcoes vem em json.itens
    mapeia: (json) => opcoesClientes(json && json.itens),
  },
  produto_id: {
    url: '/admin/descontos-produtos',
    mapeia: (json) => (Array.isArray(json) ? json : []),
  },
  produtoprincipal_id: {
    url: '/admin/descontos-produtos',
    mapeia: (json) => (Array.isArray(json) ? json : []),
  },
};

/** Itens por página nas buscas assíncronas dos combos. */
const TAKE_BUSCA = 20;

/**
 * Tipo da regra, nos mesmos cards do cadastro de campanha comercial.
 * "Padrão" é a ausência de regra por valor: o card carrega string
 * vazia porque a prop do SelectableCard é String, e a gravação recebe null.
 */
const SEM_REGRA_POR_VALOR = '';

const OPCOES_TIPO_REGRA = [
  {
    value: SEM_REGRA_POR_VALOR,
    label: 'Padrão',
    description: 'Comportamento atual: a faixa é pela quantidade do item',
    icon: 'cil-list',
  },
  {
    value: 'SEGMENTACAO',
    label: 'Segmento',
    description: 'Combinações de dimensão definem a base de cálculo',
    icon: 'cil-layers',
  },
  {
    value: 'MIX',
    label: 'Mix de Produtos',
    description: 'Lista específica de produtos participantes',
    icon: 'cil-basket',
  },
];

/**
 * Teto de espera de uma busca. `fetch` sem AbortController nunca rejeita quando
 * a request pendura, então sem isso o combo ficaria preso em "Buscando..." para
 * sempre, sem lista e sem erro.
 */
const TIMEOUT_BUSCA_MS = 10000;

const BUSCA_VAZIA = () =>
  Object.keys(CAMPOS_BUSCA_ASYNC).reduce((estado, campo) => {
    estado[campo] = { termo: '', opcoes: [], buscando: false };
    return estado;
  }, {});

const EMPTY = () => ({
  id: null,
  ativo: 1,
  aplicacallcenter: 1,
  aplicab2b: 1,
  aplicafv: 1,
  aplicaautomatico: 0,
  descricao: '',
  percdesconto: '',
  percmaximodesc: '',
  qtdmin: 0,
  qtdmax: null,
  dtinicio: null,
  dtfim: null,
  politica_id: null,
  prioritaria: 0,
  prioritariageral: 0,
  somenteretira: 0,
  planopagamento_id: null,
  multiplo: null,
  visivel: 1,
  exibeflag: 1,
  alteraprecotabela: 0,
  tabelapreco: null,
  coluna_preco: null,
  creditacontacorrente: 0,
  politicacontacorrente: 0,
  filial_id: null,
  regiao_id: null,
  praca_id: null,
  rede_id: null,
  atividade_id: null,
  cliente_id: null,
  supervisor_id: null,
  representante_id: null,
  operador_id: null,
  validausuario: 0,
  produto_id: null,
  produtoprincipal_id: null,
  fornecedor_id: null,
  dimensao1: null,
  dimensao2: null,
  dimensao3: null,
  dimensao4: null,
  dimensao5: null,
  dimensao6: null,
  dimensao7: null,
  dimensao8: null,
  dimensao9: null,
  dimensao10: null,
  tipo: null,
  valorminimo: null,
  valormaximo: null,
  validar_por_regra: 0,
  validar_todas_regras: 0,
  tipofrete_id: null,
  lock_sync: 0,
  dimensoes: [],
  mixProdutos: [],
});

export default {
  name: "DescontoFormModal",
  components: {
    CloseModal,
    InputDatePicker,
    ComboCodigo,
    HintInfo,
    DescontoCondicoes,
    ListaProdutos,
    SelectableCard,
    BooleanToggle,
    InputMoney,
  },
  props: {
    editing: { type: Object, default: null },
    dimensoes: { type: Array, default: () => [] },
    opcoesRestricoes: { type: Object, default: () => ({}) },
    erroOpcoes: { type: Boolean, default: false },
    tiposFrete: { type: Array, default: () => [] },
  },
  data() {
    return {
      form: EMPTY(),
      saving: false,
      hints: HINTS_DESCONTO,
      busca: BUSCA_VAZIA(),
      camposRestricaoCliente: CAMPOS_RESTRICAO_CLIENTE,
      camposExtra: {
        plano: CAMPOS_RESTRICAO_EXTRA.find((campo) => campo.chave === 'planos'),
        tabelapreco: CAMPOS_RESTRICAO_EXTRA.find((campo) => campo.chave === 'tabelasprecos'),
        fornecedor: CAMPOS_RESTRICAO_EXTRA.find((campo) => campo.chave === 'fornecedores'),
      },
      totalDimensoes: TOTAL_DIMENSOES,
    };
  },
  computed: {
    isEdit() {
      return !!this.editing && !!this.editing.id;
    },
    /**
     * Colunas de dimensao renderizadas: as cadastradas na base mais as que o
     * desconto em edicao ja usa (uma dimensao legada numa coluna sem cadastro
     * continuaria valendo no calculo, mas ficaria invisivel na tela).
     */
    dimensoesVisiveis() {
      return dimensoesComValoresDoForm(this.dimensoes, this.form);
    },
    /** A regra é disparada pelo valor do pedido, e não pela quantidade do item. */
    isRegraPorValor() {
      return !!this.form.tipo;
    },
    /** aplicab2b, aplicacallcenter e aplicafv — as mesmas chaves da campanha. */
    canais() {
      return CANAIS;
    },
    opcoesTipoRegra() {
      return OPCOES_TIPO_REGRA;
    },
    tipoRegraSelecionado() {
      return this.form.tipo || SEM_REGRA_POR_VALOR;
    },
  },
  watch: {
    editing: {
      immediate: true,
      handler(value) {
        const base = value ? { ...EMPTY(), ...value } : EMPTY();
        if (base.percdesconto === null || base.percdesconto === undefined) base.percdesconto = '';
        if (base.percmaximodesc === null || base.percmaximodesc === undefined) base.percmaximodesc = '';
        base.dimensoes = Array.isArray(base.dimensoes) ? base.dimensoes : [];
        base.mixProdutos = this.normalizarMix(base.mixProdutos);
        this.form = base;
        this.busca = BUSCA_VAZIA();
        this.alinharValores();
      },
    },
    dimensoes() {
      this.alinharValores();
    },
    opcoesRestricoes() {
      this.alinharValores();
    },
  },
  created() {
    // um debounce por campo: digitar em dois combos em sequencia nao deve
    // fazer um cancelar a busca do outro
    this.debouncers = Object.keys(CAMPOS_BUSCA_ASYNC).reduce((mapa, campo) => {
      mapa[campo] = _debounce(() => this.buscaOpcoes(campo), 300);
      return mapa;
    }, {});
  },
  beforeDestroy() {
    Object.values(this.debouncers).forEach((debounced) => debounced.cancel());
  },
  created() {
    this.carregarDimensoes();
    this.carregarTiposFrete();
  },
  methods: {
    close() {
      this.$modal.hide('desconto-form');
    },
    /**
     * Ajusta os valores gravados no desconto ao tipo dos ids devolvidos pela
     * API, para que os selects nao apareçam em branco ao editar.
     */
    alinharValores() {
      const patch = {
        ...patchValoresRestricoes(this.form, this.opcoesRestricoes, CAMPOS_RESTRICAO_TODOS),
        ...patchValoresDimensoes(this.form, this.dimensoes),
      };
      if (Object.keys(patch).length) {
        this.form = { ...this.form, ...patch };
      }
    },
    opcoesRestricao(campo) {
      return opcoesComValorAtual(
        opcoesDe(this.opcoesRestricoes, campo.chave),
        this.form[campo.campo]
      );
    },
    opcoesDimensao(dimensao) {
      return opcoesDimensaoComValorAtual(
        dimensao,
        this.form[`dimensao${dimensao.coluna}`]
      );
    },
    /**
     * Opcoes de um combo com busca no servidor, mantendo o valor gravado
     * selecionavel enquanto nada foi buscado.
     */
    opcoesAsync(campo) {
      return opcoesComValorAtual(this.busca[campo].opcoes, this.form[campo]);
    },
    onBuscaAsync(campo, termo) {
      const estado = this.busca[campo];

      estado.termo = (termo || '').trim();

      if (!estado.termo) {
        estado.opcoes = [];
        estado.buscando = false;
        return;
      }

      estado.buscando = true;
      this.debouncers[campo]();
    },
    buscaOpcoes(campo) {
      const estado = this.busca[campo];
      const { url, mapeia } = CAMPOS_BUSCA_ASYNC[campo];

      if (!estado.termo) {
        estado.buscando = false;
        return;
      }

      // guarda o termo desta request: sem isso, uma resposta atrasada de um
      // termo antigo sobrescreve a lista do termo atual (o debounce nao evita,
      // porque so espaca o disparo)
      const termoDaBusca = estado.termo;
      const params = new URLSearchParams({ take: TAKE_BUSCA, q: termoDaBusca });

      const timeout = setTimeout(() => {
        if (estado.termo !== termoDaBusca) return;
        estado.opcoes = [];
        estado.buscando = false;
      }, TIMEOUT_BUSCA_MS);

      get(`${url}?${params.toString()}`)
        .then((json) => {
          if (estado.termo !== termoDaBusca) return;
          estado.opcoes = mapeia(json);
          estado.buscando = false;
        })
        .catch(() => {
          if (estado.termo !== termoDaBusca) return;
          estado.opcoes = [];
          estado.buscando = false;
        })
        .finally(() => clearTimeout(timeout));
    },
    /**
     * O toggle e o card trabalham com booleano; as colunas guardam 1/0, como
     * os selects da tela gravam.
     */
    definirBooleano(campo, valor) {
      this.form[campo] = valor ? 1 : 0;
    },
    alternarCanal(campo) {
      this.definirBooleano(campo, !this.form[campo]);
    },
    /**
     * O card devolve string; `tipo` continua guardando null, SEGMENTACAO ou MIX.
     */
    selecionarTipoRegra(valor) {
      this.form.tipo = valor === SEM_REGRA_POR_VALOR ? null : valor;
    },
    /**
     * O back devolve o mix como produto_id (+ produto, quando carregado);
     * a lista de produtos trabalha com o objeto do produto.
     */
    normalizarMix(mix) {
      if (!Array.isArray(mix)) return [];

      return mix.map((linha) => ({
        ...(linha.produto || {}),
        id: linha.produto_id !== undefined ? linha.produto_id : linha.id,
        percDesc: linha.percdesconto !== undefined ? linha.percdesconto : null,
        percMaxDesc: linha.percmaximodesc !== undefined ? linha.percmaximodesc : null,
      }));
    },
    adicionarProdutoMix(produto) {
      this.form.mixProdutos.push(produto);
    },
    removerProdutoMix(produtoId) {
      this.form.mixProdutos = this.form.mixProdutos.filter(
        (produto) => String(produto.id) !== String(produtoId)
      );
    },
    /**
     * O ListaProdutos entrega o ÍNDICE da linha, não o id do produto — é o que a
     * campanha comercial já consome (updateProdutoMixPercentage). Procurar por id
     * aqui não casava com nada e o percentual nunca chegava ao produto.
     *
     * As chaves também são as do componente: ele lê `percDesc` e `percMaxDesc`. A
     * tradução para as colunas do banco acontece no payload().
     */
    atualizarPercentualMix(index, percentual) {
      const produto = this.form.mixProdutos[index];

      if (produto) {
        this.$set(produto, 'percDesc', percentual);
      }
    },
    atualizarTetoMix(index, teto) {
      const produto = this.form.mixProdutos[index];

      if (produto) {
        this.$set(produto, 'percMaxDesc', teto);
      }
    },
    payload() {
      const item = { ...this.form };
      if (item.percdesconto === '') item.percdesconto = null;
      if (item.percmaximodesc === '' || item.percmaximodesc === 0) item.percmaximodesc = null;

      if (!item.tipo) {
        // Sem regra por valor, os campos da regra composta não vão no payload
        // para não gravar faixa e condição em desconto por quantidade.
        item.valorminimo = null;
        item.valormaximo = null;
        item.validar_por_regra = 0;
        item.validar_todas_regras = 0;
        item.dimensoes = [];
        item.mixProdutos = [];

        return item;
      }

      // Campos do cabeçalho que a tela esconde na regra por valor: sem zerar aqui,
      // um desconto por quantidade convertido para MIX guardaria o percentual
      // antigo invisível, e o backend o publica como `perc` para os quatro fronts.
      item.percdesconto = null;
      item.percmaximodesc = null;

      // A faixa de quantidade é do desconto padrão. Em Segmento e Mix ela não
      // valida nada, e deixá-la gravada faria a regra parecer ter duas faixas.
      item.qtdmin = null;
      item.qtdmax = null;

      item.dimensoes = item.tipo === 'SEGMENTACAO' ? item.dimensoes : [];
      item.mixProdutos = item.tipo === 'MIX'
        ? item.mixProdutos.map((produto) => ({
          produto_id: produto.id,
          percdesconto: produto.percDesc !== undefined ? produto.percDesc : null,
          percmaximodesc: produto.percMaxDesc !== undefined ? produto.percMaxDesc : null,
        }))
        : [];

      return item;
    },
    validate() {
      const missing = [];
      if (!this.form.id && this.form.id !== 0) missing.push('Cód');
      // O % Desc. do cabeçalho só existe no desconto por quantidade; na regra por
      // valor quem carrega o percentual é a condição ou o produto do mix.
      if (!this.isRegraPorValor
        && (this.form.percdesconto === '' || this.form.percdesconto === null || this.form.percdesconto === undefined)) {
        missing.push('% Desc.');
      }
      // Qtd. Mín só existe no desconto padrão: em Segmento e Mix a faixa é de valor.
      if (!this.isRegraPorValor
        && (this.form.qtdmin === '' || this.form.qtdmin === null || this.form.qtdmin === undefined)) {
        missing.push('Qtd. Mín');
      }
      if (!this.form.dtinicio) missing.push('Dt. início');
      if (!this.form.dtfim) missing.push('Dt. fim');
      return missing;
    },
    /**
     * Regra por valor sem faixa e sem condição vale sobre o pedido inteiro —
     * desconto a mais, e em silêncio. É o pior desfecho possível deste cadastro.
     */
    validateRegraPorValor() {
      if (!this.isRegraPorValor) return null;

      if (this.form.tipo === 'MIX' && !this.form.mixProdutos.length) {
        return 'Uma regra de mix precisa de ao menos um produto selecionado.';
      }

      if (this.form.tipo === 'SEGMENTACAO' && !this.form.dimensoes.length) {
        return 'Uma regra de segmento precisa de ao menos uma condição cadastrada.';
      }

      if (this.form.validar_por_regra) {
        const semFaixa = this.form.dimensoes.some(
          (condicao) => !condicao.valorminimo && !condicao.valormaximo
        );

        if (semFaixa) {
          return 'Com faixa por condição, cada condição precisa de valor mínimo ou máximo.';
        }

        return null;
      }

      if (!this.form.valorminimo && !this.form.valormaximo) {
        return 'Informe o valor mínimo ou o valor máximo do pedido para a regra.';
      }

      return null;
    },
    submit() {
      if (this.saving) return;
      const missing = this.validate();
      if (missing.length) {
        new window.swal({
          title: 'Campos obrigatórios não preenchidos',
          text: missing.join(', '),
          icon: 'warning',
        });
        return;
      }

      const erroRegra = this.validateRegraPorValor();
      if (erroRegra) {
        new window.swal({
          title: 'Regra por valor incompleta',
          text: erroRegra,
          icon: 'warning',
        });
        return;
      }

      const item = this.payload();
      this.saving = true;

      const onSuccess = (json) => {
        this.saving = false;
        new window.swal({
          title: json && json.message ? json.message : 'Salvo com sucesso',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false,
        });
        this.$emit('saved');
        this.close();
      };
      const onError = (err) => {
        this.saving = false;
        new window.swal({
          title: 'Falha ao salvar',
          text: err && err.message ? err.message : 'Erro inesperado',
          icon: 'error',
        });
      };

      const request = this.isEdit
        ? put(`/admin/descontos/${encodeURIComponent(item.id)}`, item)
        : post('/admin/descontos', item);
      request.then(onSuccess).catch(onError);
    },
  },
};
</script>

<style scoped>
.form-title {
  font-weight: 600;
  margin-bottom: 16px;
}

/* Mesmo tratamento de `.section-label` em StepInformacoes/StepRegras. */
.section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #888;
  letter-spacing: 0.5px;
  margin: 18px 0 6px;
}

/* Tipografia dos campos igual à do cadastro de campanha comercial: os valores
   sao os de `.field-label` em sections/StepInformacoes.vue. Redefinir a classe
   aqui alcanca os campos todos sem tocar em cada um deles no template. */
.form-label {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 3px;
  display: block;
}

.form-group {
  margin-bottom: 10px;
}

.req {
  color: var(--danger);
}

.row {
  row-gap: 12px;
}

.row > [class^="col-"] {
  margin-bottom: 4px;
}

/* O Tipo de frete só aparece na regra por valor, então colunas fixas somariam 12
   num estado e menos no outro. Aqui as colunas dividem a sobra: Qtd. Mín e Máx
   ficam estreitas e o resto cresce, preenchendo a linha com ou sem o frete. */
.row--aplicacao > [class^="col-"] {
  flex: 1 1 160px;
  max-width: none;
}

.row--aplicacao > .campo-qtd {
  flex: 0 0 110px;
  max-width: 110px;
}

/* Aba Regra: mesmas grades do cadastro de campanha comercial —
   TipoRegraCards.vue para os cards e StepRegras.vue para a caixa de toggles. */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

@media (max-width: 768px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }
}

.restricao-ajuda {
  font-size: 12px;
  color: #667085;
  margin-bottom: 12px;
}

.toggles-box {
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  padding: 2px 14px;
}

.modal-footer-bar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid #e9ecef;
  background: #fff;
}
</style>
